/**
 * Reliability Alert Store – Phase 15 (Backend-connected)
 *
 * All alert CRUD & workflow actions go through /api/alerts/ endpoints.
 * Falls back to localStorage if backend is unreachable.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

function authHeaders() {
  // Check multiple token keys used by different parts of the app
  const token = localStorage.getItem('backoffice_token') || localStorage.getItem('token') || localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ─── Read ───

export async function getAlerts(filters = {}) {
  try {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.severity) params.severity = filters.severity;
    if (filters.search) params.search = filters.search;

    const { data } = await axios.get(`${API}/api/alerts`, {
      headers: authHeaders(),
      params,
    });
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn('[AlertStore] Backend unreachable, using localStorage fallback', err.message);
    return _lsFallbackList(filters);
  }
}

export async function getAlertsSummary() {
  try {
    const { data } = await axios.get(`${API}/api/alerts/summary`, {
      headers: authHeaders(),
    });
    return data;
  } catch (err) {
    console.warn('[AlertStore] Summary fetch failed', err.message);
    const all = await getAlerts({ status: 'open' });
    return _deriveSummary(all);
  }
}

export async function getAlertById(alertId) {
  try {
    const { data } = await axios.get(`${API}/api/alerts/${alertId}`, {
      headers: authHeaders(),
    });
    return data;
  } catch {
    return null;
  }
}

// ─── Workflow Actions ───

export async function acknowledgeAlert(alertId, note = '', userId = '') {
  const { data } = await axios.post(
    `${API}/api/alerts/${alertId}/acknowledge`,
    { note, user_id: userId },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function escalateAlert(alertId, note = '', userId = '') {
  const { data } = await axios.post(
    `${API}/api/alerts/${alertId}/escalate`,
    { note, user_id: userId },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function resolveAlert(alertId, note = '', userId = '') {
  const { data } = await axios.post(
    `${API}/api/alerts/${alertId}/resolve`,
    { note, user_id: userId },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function dismissAlert(alertId, reason, userId = '') {
  const { data } = await axios.post(
    `${API}/api/alerts/${alertId}/dismiss`,
    { note: reason, user_id: userId },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function addAlertNote(alertId, note, userId = '') {
  const { data } = await axios.post(
    `${API}/api/alerts/${alertId}/notes`,
    { note, user_id: userId },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
}

// ─── Evaluate (trigger backend anomaly scan) ───

export async function evaluateAlerts() {
  const { data } = await axios.post(
    `${API}/api/alerts/evaluate`,
    {},
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
}

// ─── Local Storage Fallback ───

const LS_KEY = 'asterpos_reliability_alerts';

function _lsFallbackList(filters = {}) {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    let items = JSON.parse(raw);
    if (filters.status === 'open') {
      items = items.filter(a => ['open', 'acknowledged', 'escalated'].includes(a.status));
    } else if (filters.status) {
      items = items.filter(a => a.status === filters.status);
    }
    if (filters.severity) {
      items = items.filter(a => a.severity === filters.severity);
    }
    return items;
  } catch {
    return [];
  }
}

function _deriveSummary(alerts) {
  return {
    open: alerts.filter(a => a.status === 'open').length,
    acknowledged: alerts.filter(a => a.status === 'acknowledged').length,
    escalated: alerts.filter(a => a.status === 'escalated').length,
    resolved: 0,
    dismissed: 0,
    critical: alerts.filter(a => a.severity === 'critical').length,
    error: alerts.filter(a => a.severity === 'error').length,
    warning: alerts.filter(a => a.severity === 'warning').length,
    info: alerts.filter(a => a.severity === 'info').length,
    unacknowledged: alerts.filter(a => a.status === 'open').length,
    total: alerts.length,
  };
}

// ─── Compat aliases for UI imports ───
export const getActiveAlerts = (filters = {}) => {
  const mapped = {};
  if (filters.openOnly) mapped.status = 'open';
  else if (filters.status) mapped.status = filters.status;
  if (filters.severity) mapped.severity = filters.severity;
  if (filters.searchText) mapped.search = filters.searchText;
  return getAlerts(mapped);
};
export const getAlertSummary = getAlertsSummary;

// Legacy compat - no-ops for functions the UI pages might import
export function getSuppressedAlerts() { return []; }
export function cleanupOldAlerts() { }

export default {
  getAlerts,
  getActiveAlerts,
  getAlertsSummary,
  getAlertSummary,
  getAlertById,
  acknowledgeAlert,
  escalateAlert,
  resolveAlert,
  dismissAlert,
  addAlertNote,
  evaluateAlerts,
  getSuppressedAlerts,
  cleanupOldAlerts,
};
